import { useState, useEffect } from "react";
import { FileText, Calendar, ArrowLeft, MessageCircle, Send, Share2, Facebook, Twitter, Linkedin, Link2, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { usePublicData } from "../../hooks/usePublicData";
import { getBlogComments, createBlogComment } from "../../lib/api";
import { toast } from "sonner";

export default function PublicBlog() {
  const { data, loading, token } = usePublicData();
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentForm, setCommentForm] = useState({ author_name: "", content: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (selectedPost) {
      fetchComments(selectedPost.id);
    }
  }, [selectedPost]);

  const fetchComments = async (postId) => {
    setLoadingComments(true);
    try {
      const res = await getBlogComments(postId);
      setComments(res.data || []);
    } catch (error) {
      console.error("Failed to load comments");
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.author_name.trim() || !commentForm.content.trim()) {
      toast.error("Please fill in your name and comment");
      return;
    }

    setSubmitting(true);
    try {
      await createBlogComment(selectedPost.id, commentForm);
      toast.success("Comment posted!");
      setCommentForm({ author_name: "", content: "" });
      fetchComments(selectedPost.id);
    } catch (error) {
      toast.error("Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const getShareUrl = () => {
    return `${window.location.origin}/public/${token}/blog?post=${selectedPost?.id}`;
  };

  const shareOn = (platform) => {
    const url = encodeURIComponent(getShareUrl());
    const title = encodeURIComponent(selectedPost?.title || "");
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
    };
    
    window.open(urls[platform], "_blank", "width=600,height=400");
    setShowShareMenu(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied to clipboard!");
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { blog_posts } = data;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateStr);
  };

  if (selectedPost) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedPost(null);
              setComments([]);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {/* Share Button */}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowShareMenu(!showShareMenu)}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            
            {showShareMenu && (
              <div className="absolute right-0 top-full mt-2 bg-card border border-border rounded-xl p-2 shadow-lg z-50 min-w-[180px]">
                <button
                  onClick={() => shareOn("facebook")}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left"
                >
                  <Facebook className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Facebook</span>
                </button>
                <button
                  onClick={() => shareOn("twitter")}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left"
                >
                  <Twitter className="w-4 h-4 text-sky-500" />
                  <span className="text-sm">Twitter</span>
                </button>
                <button
                  onClick={() => shareOn("linkedin")}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left"
                >
                  <Linkedin className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">LinkedIn</span>
                </button>
                <button
                  onClick={() => shareOn("whatsapp")}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-sm">WhatsApp</span>
                </button>
                <div className="border-t border-border my-1"></div>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
                  <span className="text-sm">{copied ? "Copied!" : "Copy Link"}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <article className="bg-card border border-border rounded-xl overflow-hidden">
          {selectedPost.image && (
            <img
              src={selectedPost.image}
              alt={selectedPost.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          )}
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Calendar className="w-4 h-4" />
              {formatDate(selectedPost.created_at)}
              {selectedPost.author && (
                <>
                  <span>•</span>
                  <span>By {selectedPost.author}</span>
                </>
              )}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-6">
              {selectedPost.title}
            </h1>
            <div className="prose prose-invert max-w-none">
              {selectedPost.content.split('\n').map((paragraph, i) => (
                <p key={i} className="text-foreground/80 mb-4">{paragraph}</p>
              ))}
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Comments ({comments.length})
          </h2>

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="mb-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                placeholder="Your name"
                value={commentForm.author_name}
                onChange={(e) => setCommentForm(prev => ({ ...prev, author_name: e.target.value }))}
                className="bg-background border-border"
              />
            </div>
            <Textarea
              placeholder="Write a comment..."
              value={commentForm.content}
              onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
              rows={3}
              className="bg-background border-border"
            />
            <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground">
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "Posting..." : "Post Comment"}
            </Button>
          </form>

          {/* Comments List */}
          {loadingComments ? (
            <div className="text-center py-8 text-muted-foreground">Loading comments...</div>
          ) : comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="p-4 bg-secondary/50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{comment.author_name}</span>
                    <span className="text-xs text-muted-foreground">{formatCommentDate(comment.created_at)}</span>
                  </div>
                  <p className="text-foreground/80">{comment.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No comments yet. Be the first to comment!</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Blog
        </h1>
        <p className="text-muted-foreground mt-1">
          Tournament news and updates
        </p>
      </div>

      {blog_posts && blog_posts.length > 0 ? (
        <div className="grid gap-6">
          {blog_posts
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map((post) => (
              <article
                key={post.id}
                className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                <div className="md:flex">
                  {post.image && (
                    <div className="md:w-72 flex-shrink-0">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-48 md:h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.created_at)}
                    </div>
                    <h2 className="font-heading text-xl font-bold mb-2 hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-muted-foreground line-clamp-3">
                      {post.content}
                    </p>
                    <button className="mt-4 text-primary text-sm font-medium hover:underline">
                      Read more →
                    </button>
                  </div>
                </div>
              </article>
            ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">No blog posts yet</p>
        </div>
      )}
    </div>
  );
}
